import { ReactNode, createContext, useContext, useState } from 'react';
import { notification } from 'antd';
import { DarwinAlert } from 'components/darwin-ui/Alert';
import { DarwinFlex } from 'components/darwin-ui';
import styles from './FloatNotification.module.css';

type NotificationType = 'error' | 'info' | 'success' | 'warning';
type DisplayMode = 'notification' | 'alert';

type AlertInfo = {
  id: number;
  title: string;
  description: string;
  type: NotificationType;
};

type NotificationContextType = {
  openNotification: (
    title: string,
    description: string,
    type?: NotificationType,
    mode?: DisplayMode
  ) => void;
};

const Context = createContext<NotificationContextType | undefined>(undefined);

export function useFloatNotification() {
  const context = useContext(Context);
  if (!context) throw new Error('Notification provider not found');
  return context;
}

export function NotificationContext({ children }: { children: ReactNode }) {
  const [api, contextHolder] = notification.useNotification();
  const [alerts, setAlerts] = useState<AlertInfo[]>([]);

  const openNotification = (
    title: string,
    description: string,
    type: NotificationType = 'success',
    mode: DisplayMode = 'notification'
  ) => {
    if (mode === 'notification') {
      api[type]({
        message: title,
        description,
        placement: 'topRight',
      });
    }

    if (mode === 'alert') {
      const newAlert: AlertInfo = {
        id: Date.now(), // Unic ID based on time
        title,
        description,
        type,
      };

      setAlerts(prevAlerts => [...prevAlerts, newAlert]);

      // Hide alert after 5 seconds
      setTimeout(() => {
        setAlerts(prevAlerts =>
          prevAlerts.filter(alert => alert.id !== newAlert.id)
        );
      }, 5000);
    }
  };

  return (
    <Context.Provider value={{ openNotification }}>
      {contextHolder}
      {children}

      {/* Contenedor de alertas acumuladas */}
      <DarwinFlex className={styles.notificationContainer}>
        {alerts?.map(alert => (
          <DarwinAlert
            key={alert.id}
            description={alert.description}
            type={alert.type}
            showIcon
            className={styles.darwinAlert}
          />
        ))}
      </DarwinFlex>
    </Context.Provider>
  );
}
